"use client";

import { useEffect, useRef, useState } from "react";
import { ChevronDown, Mail, MapPin, Phone } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import MobileHeaderMenu from "@/components/common/MobileHeaderMenu";
import { Button } from "@/components/ui/button";
import { serviceAreas as serviceAreaData } from "@/data/serviceArea";

const navItems = [
  { label: "Home", href: "/" },
  { label: "About Us", href: "/about-us" },
  { label: "Gallery", href: "/gallery" },
  { label: "Reviews", href: "/reviews" },
  { label: "Blogs", href: "/blog" },
  { label: "FAQs", href: "/faqs" },
  { label: "Contact Us", href: "/contact-us" },
];

const leftNavItems = navItems.slice(0, 2); // Home, About Us
const galleryNavItem = navItems[2];
const rightNavItems = navItems.slice(3); // Reviews, Blogs, FAQs, Contact Us

// Nested structure for the desktop dropdown
const serviceCategories = [
  {
    label: "Terrazzo",
    items: [
      { label: "Terrazzo Restoration", href: "/services/terrazzo-restoration" },
      { label: "Terrazzo Cleaning", href: "/services/terrazzo-cleaning" },
      { label: "Terrazzo Repairs", href: "/services/terrazzo-repairs" },
      { label: "Terrazzo Sealing", href: "/services/terrazzo-sealing" },
      { label: "Terrazzo Polishing", href: "/services/terrazzo-polishing" },
    ],
  },
  {
    label: "Marble",
    items: [
      { label: "Marble Restoration", href: "/services/marble-restoration" },
      { label: "Marble Cleaning", href: "/services/marble-cleaning" },
      { label: "Marble Repair", href: "/services/marble-repair" },
      { label: "Marble Sealing", href: "/services/marble-sealing" },
      { label: "Marble Polishing", href: "/services/marble-polishing" },
    ],
  },
  {
    label: "Travertine",
    items: [
      { label: "Travertine Restoration", href: "/services/travertine-restoration" },
      { label: "Travertine Cleaning", href: "/services/travertine-cleaning" },
      { label: "Travertine Repair", href: "/services/travertine-repair" },
      { label: "Travertine Sealing", href: "/services/travertine-sealing" },
      { label: "Travertine Polishing", href: "/services/travertine-polishing" },
    ],
  },
  {
    label: "Hardscaping",
    items: [
      { label: "Hardscaping Services", href: "/services/hardscaping-services" },
      { label: "Hardscape Design", href: "/services/hardscape-design-services" },
      { label: "Hardscape Installation", href: "/services/hardscape-installation" },
      { label: "Hardscape Restoration", href: "/services/hardscape-restoration" },
      { label: "Hardscape Maintenance", href: "/services/hardscape-maintenance" },
    ],
  },
  {
    label: "Concrete",
    items: [
      { label: "Concrete Restoration", href: "/services/concrete-restoration" },
      { label: "Concrete Cleaning", href: "/services/concrete-cleaning" },
      { label: "Concrete Repair", href: "/services/concrete-repair" },
      { label: "Concrete Sealing", href: "/services/concrete-sealing" },
      { label: "Concrete Polishing", href: "/services/concrete-polishing" },
    ],
  },
];

const serviceAreas = serviceAreaData.map(({ name, slug }) => ({
  label: name,
  href: `/service-area/${slug}`,
}));

const serviceCounties = ["Hillsborough County", "Pinellas County", "Pasco County"];

const contactInfo = {
  address: "1409 Sunnyhills Dr, Brandon, FL 33510, USA",
  addressHref: "https://maps.app.goo.gl/J1acKa65Xj5rqWa28",
  phone: "813-842-2255",
  phoneHref: "tel:+18138422255",
  email: "bestchoice2022@gmail.com",
  emailHref: "mailto:bestchoice2022@gmail.com",
};

export default function Header() {
  const [isServiceAreasOpen, setIsServiceAreasOpen] = useState(false);
  const serviceAreasRef = useRef<HTMLLIElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        serviceAreasRef.current &&
        !serviceAreasRef.current.contains(event.target as Node)
      ) {
        setIsServiceAreasOpen(false);
      }
    };

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setIsServiceAreasOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, []);

  // Flatten categories for the mobile menu (flat array with href)
  return (
    <>
      {/* Top contact bar */}
      <div className="hidden bg-[#46473f] px-4 py-2 text-sm text-white xl:flex xl:justify-end xl:gap-6">
        <div className="flex items-center gap-2">
          <MapPin className="h-4 w-4 text-blue-400" />
          <Link
            href={contactInfo.addressHref}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-300 transition-colors hover:text-white"
          >
            {contactInfo.address}
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <Phone className="h-4 w-4 text-green-400" />
          <Link
            href={contactInfo.phoneHref}
            className="text-gray-300 transition-colors hover:text-white"
          >
            {contactInfo.phone}
          </Link>
        </div>
        <div className="flex items-center gap-2">
          <Mail className="h-4 w-4 text-yellow-400" />
          <Link
            href={contactInfo.emailHref}
            className="text-gray-300 transition-colors hover:text-white"
          >
            {contactInfo.email}
          </Link>
        </div>
      </div>

      <header className="sticky top-0 z-50 w-full border-b bg-[#fcf9f2] shadow-sm">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="relative flex h-[72px] items-center justify-end lg:grid lg:h-[84px] lg:grid-cols-[minmax(0,1fr)_18rem_minmax(0,1fr)] xl:grid-cols-[minmax(0,1fr)_20rem_minmax(0,1fr)]">
            {/* Left navigation (Home, About Us, dropdowns, Gallery) */}
            <nav className="hidden xl:block">
              <ul className="flex items-center justify-end gap-3 whitespace-nowrap pr-5 xl:gap-5 2xl:gap-7">
                {leftNavItems.map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      className="group relative text-base font-medium text-gray-700 transition-colors duration-300 hover:text-[#46473f]"
                    >
                      {item.label}
                      <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-[#46473f] transition-all duration-300 group-hover:w-full" />
                    </Link>
                  </li>
                ))}

                {/* Services – two-level flyout dropdown */}
                <li className="group relative">
                  <button
                    type="button"
                    className="flex items-center text-base font-medium text-gray-700 transition-colors duration-300 hover:text-[#46473f]"
                    aria-label="Services"
                  >
                    Services
                    <ChevronDown className="ml-1 h-4 w-4 transition-transform duration-300 group-hover:rotate-180" />
                  </button>

                  {/* First level: category list */}
                  <div className="invisible absolute left-0 top-full z-50 pt-2 w-56 opacity-0 transition-all duration-200 group-hover:visible group-hover:opacity-100 group-focus-within:visible group-focus-within:opacity-100">
                    <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                      <ul className="space-y-0.5">
                        {serviceCategories.map((category) => (
                          <li key={category.label} className="relative group/cat">
                            <button
                              type="button"
                              className="flex w-full items-center justify-between rounded-lg px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[#faf6ee] hover:text-[#8a6a3c] transition-colors"
                            >
                              {category.label}
                              <ChevronDown className="h-3.5 w-3.5 -rotate-90 text-gray-400" />
                            </button>

                            {/* Second level: submenu */}
                            <div className="invisible absolute left-full top-0 z-50 pl-2 w-56 opacity-0 transition-all duration-150 group-hover/cat:visible group-hover/cat:opacity-100">
                              <div className="rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                                <ul className="space-y-0.5">
                                  {category.items.map((item) => (
                                    <li key={item.href}>
                                      <Link
                                        href={item.href}
                                        className="block rounded-lg px-4 py-2 text-sm text-gray-700 hover:bg-[#faf6ee] hover:text-[#8a6a3c] transition-colors"
                                      >
                                        {item.label}
                                      </Link>
                                    </li>
                                  ))}
                                </ul>
                              </div>
                            </div>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </li>

                {/* Service area dropdown */}
                <li
                  ref={serviceAreasRef}
                  className="relative"
                  onMouseEnter={() => setIsServiceAreasOpen(true)}
                  onMouseLeave={() => setIsServiceAreasOpen(false)}
                >
                  <button
                    type="button"
                    className="flex items-center text-base font-medium text-gray-700 transition-colors duration-300 hover:text-[#46473f]"
                    aria-label="Service Areas"
                    aria-expanded={isServiceAreasOpen}
                    onClick={() => setIsServiceAreasOpen((isOpen) => !isOpen)}
                  >
                    Service Areas
                    <ChevronDown className={`ml-1 h-4 w-4 transition-transform duration-300 ${isServiceAreasOpen ? "rotate-180" : ""}`} />
                  </button>

                  <div
                    className={`absolute left-0 top-full z-50 pt-2 w-64 transition-all duration-200 ${
                      isServiceAreasOpen
                        ? "visible opacity-100 translate-y-0"
                        : "invisible opacity-0 translate-y-1 pointer-events-none"
                    }`}
                  >
                    <div className="max-h-[calc(100vh-7rem)] overflow-y-auto overscroll-contain rounded-xl border border-gray-200 bg-white p-2 shadow-xl">
                      <ul className="space-y-0.5">
                        {serviceAreas.map((area) => (
                          <li key={area.href}>
                            <Link
                              href={area.href}
                              className="block rounded-lg px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-[#faf6ee] hover:text-[#8a6a3c]"
                              onClick={() => setIsServiceAreasOpen(false)}
                            >
                              {area.label}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </li>

                <li>
                  <Link
                    href={galleryNavItem.href}
                    className="group relative text-base font-medium text-gray-700 transition-colors duration-300 hover:text-[#46473f]"
                  >
                    {galleryNavItem.label}
                    <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-[#46473f] transition-all duration-300 group-hover:w-full" />
                  </Link>
                </li>
              </ul>
            </nav>

            {/* Center logo & phone */}
            <div className="absolute left-0 top-2 z-10 flex flex-col items-center translate-x-0 lg:left-1/2 lg:top-1 lg:-translate-x-1/2">
              <Link
                href="/"
                className="relative block size-32 overflow-hidden rounded-sm border-4 border-[#fcf9f2] bg-white shadow-lg transition-transform hover:scale-105 lg:size-40"
                aria-label="Tampa Terrazzo home"
              >
                <Image
                  src="/sitelogo.png"
                  alt="Tampa Terrazzo logo"
                  fill
                  priority
                  sizes="(min-width: 1024px) 160px, 96px"
                  className="object-cover object-left"
                />
              </Link>

            </div>

            {/* Right navigation (Reviews, Blogs, FAQs, Contact Us + button) */}
            <nav className="hidden xl:col-start-3 xl:block">
              <ul className="flex items-center justify-start gap-3 whitespace-nowrap pl-5 xl:gap-5 2xl:gap-7">
                {rightNavItems.map((item) => (
                  <li key={item.label}>
                    <Link
                      href={item.href}
                      className="group relative text-base font-medium text-gray-700 transition-colors duration-300 hover:text-[#46473f]"
                    >
                      {item.label}
                      <span className="absolute -bottom-1 left-0 h-0.5 w-0 bg-[#46473f] transition-all duration-300 group-hover:w-full" />
                    </Link>
                  </li>
                ))}
                <Button
                  asChild
                  className="hidden rounded-full bg-[#46473f] px-5 py-2 text-white shadow-md transition-all duration-300 hover:scale-105 hover:bg-[#35362f] hover:shadow-lg xl:inline-flex 2xl:px-6"
                >
                  <Link href="/get-free-estimate">Get Free Estimate</Link>
                </Button>
              </ul>
            </nav>

            {/* Mobile menu – flat service list passed here */}
            <div className="xl:hidden">
              <MobileHeaderMenu
                contactInfo={contactInfo}
                navItems={navItems}
                serviceCategories={serviceCategories}
                serviceAreas={serviceAreas}
                serviceCounties={serviceCounties}
              />
            </div>
          </div>
        </div>
      </header>
    </>
  );
}
